import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectOperation, selectCaptureConfigurations } from '../app/reducers/operation';
import { selectGooglePayNetworks } from '../app/reducers/paymentMethod';
import { getCurrencyAlpha, formatAmountDecimal } from '../helpers/currency';
import { getConstant, GOOGLE_PAY_ENV, GOOGLE_PAY_MERCHANT_ID } from '../constants';

const CARD_PAYMENT_METHOD_TYPE = 'CARD';
const ALLOWED_CARD_AUTH_METHODS = ['PAN_ONLY'];
const DEFAULT_CARD_NETWORKS = ['VISA', 'MASTERCARD'];
const GATEWAY = 'alignet';
const GOOGLE_PAY_SDK_SCRIPT_ID = 'google-pay-sdk';
const GOOGLE_PAY_ENVIRONMENTS = ['TEST', 'PRODUCTION'];
const GOOGLE_PAY_SDK_URL = 'https://pay.google.com/gp/p/js/pay.js';
const GOOGLE_PAY_SDK_LOAD_TIMEOUT_MS = 10000;
const GOOGLE_PAY_IS_READY_TO_PAY_TIMEOUT_MS = 5000;


let googlePaySdkPromise = null;

const loadGooglePaySdk = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('Google Pay SDK no disponible fuera del navegador'));
  }
  const googleGlobal = window['google'];
  if (googleGlobal && googleGlobal.payments && googleGlobal.payments.api) {
    return Promise.resolve();
  }
  if (googlePaySdkPromise !== null) {
    return googlePaySdkPromise;
  }
  
  const thisPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(GOOGLE_PAY_SDK_SCRIPT_ID);
    const clearIfCurrent = () => {
      if (googlePaySdkPromise === thisPromise) {
        googlePaySdkPromise = null;
      }
    };
    const removeFailedScript = () => {
      const el = document.getElementById(GOOGLE_PAY_SDK_SCRIPT_ID);
      if (el && el.parentNode) {
        el.parentNode.removeChild(el);
      }
    };
    const timeoutId = setTimeout(() => {
      clearIfCurrent();
      removeFailedScript();
      reject(new Error('Timeout al cargar pay.js'));
    }, GOOGLE_PAY_SDK_LOAD_TIMEOUT_MS);
    const onReady = () => {
      clearTimeout(timeoutId);
      const g = window['google'];
      if (g && g.payments && g.payments.api) {
        resolve();
      } else {
        clearIfCurrent();
        removeFailedScript();
        reject(new Error('Google Pay SDK cargado pero payments.api no disponible'));
      }
    };
    if (existing) {
      existing.addEventListener('load', onReady);
      existing.addEventListener('error', () => {
        clearTimeout(timeoutId);
        clearIfCurrent();
        removeFailedScript();
        reject(new Error('Fallo al cargar pay.js'));
      });
      return;
    }
    const script = document.createElement('script');
    script.id = GOOGLE_PAY_SDK_SCRIPT_ID;
    script.src = GOOGLE_PAY_SDK_URL;
    script.async = true;
    script.onload = onReady;
    script.onerror = () => {
      clearTimeout(timeoutId);
      clearIfCurrent();
      removeFailedScript();
      reject(new Error('Fallo al cargar pay.js'));
    };
    document.head.appendChild(script);
  });
  googlePaySdkPromise = thisPromise;
  return googlePaySdkPromise;
};


const resolveGooglePayEnvironment = () => {
  const env = getConstant(GOOGLE_PAY_ENV);
  return GOOGLE_PAY_ENVIRONMENTS.includes(env) ? env : 'TEST';
};


export const useGooglePay = () => {
  const operation = useSelector(selectOperation);
  const captureConfigurations = useSelector(selectCaptureConfigurations);
  const googlePayNetworks = useSelector(selectGooglePayNetworks);
  const allowedCardNetworks = (Array.isArray(googlePayNetworks) && googlePayNetworks.length > 0)
    ? googlePayNetworks
    : DEFAULT_CARD_NETWORKS;

  const paymentsClientRef = useRef(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(null);

  const environment = resolveGooglePayEnvironment();
  const isEnvironmentValid = GOOGLE_PAY_ENVIRONMENTS.includes(environment);
  const gatewayMerchantId = operation && operation.merchant_code;
  const merchantName = (captureConfigurations && captureConfigurations.merchant_name) || '';
  const merchantId = getConstant(GOOGLE_PAY_MERCHANT_ID) || '';


  const getBaseCardPaymentMethod = useCallback(() => ({
    type: CARD_PAYMENT_METHOD_TYPE,
    parameters: {
      allowedAuthMethods: ALLOWED_CARD_AUTH_METHODS,
      allowedCardNetworks: allowedCardNetworks
    }
  }), [allowedCardNetworks]);

  const getTokenizationSpecification = useCallback(() => ({
    type: 'PAYMENT_GATEWAY',
    parameters: {
      gateway: GATEWAY,
      gatewayMerchantId: gatewayMerchantId
    }
  }), [gatewayMerchantId]);

  const getTransactionInfo = useCallback(() => {
    const paymentDetails = operation && operation.payment_details;
    if (!paymentDetails) {
      throw new Error('payment_details no disponible en la operacion');
    }

    const billing = paymentDetails.billing || {};
    const location = billing.location || {};
    const countryCode = location.country;

    const transactionInfo = {
      totalPriceStatus: 'FINAL',
      totalPrice: formatAmountDecimal(paymentDetails.amount, paymentDetails.currency),
      currencyCode: getCurrencyAlpha(paymentDetails.currency)
    };

    if (countryCode) {
      transactionInfo.countryCode = countryCode;
    }
    return transactionInfo;
  }, [operation]);

  const getPaymentsClient = useCallback(() => {
    if (!isEnvironmentValid) {
      throw new Error('Google Pay environment invalido o ausente');
    }
    const g = typeof window !== 'undefined' ? window['google'] : undefined;
    if (!g || !g.payments || !g.payments.api) {
      throw new Error('Google Pay SDK (pay.js) no disponible');
    }
    if (paymentsClientRef.current === null) {
      paymentsClientRef.current = new g.payments.api.PaymentsClient({
        environment: environment
      });
    }
    return paymentsClientRef.current;
  }, [environment, isEnvironmentValid]);

  const isReadyToPay = useCallback(async () => {
    try {
      const client = getPaymentsClient();
      const request = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: [getBaseCardPaymentMethod()]
      };

      let timeoutId;
      const timeout = new Promise((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error("Timeout en isReadyToPay")),
          GOOGLE_PAY_IS_READY_TO_PAY_TIMEOUT_MS
        );
      });
      
      const response = await Promise.race([
        client.isReadyToPay(request),
        timeout
      ]).finally(() => clearTimeout(timeoutId));
      return Boolean(response && response.result);
    } catch (e) {
      console.error('useGooglePay.isReadyToPay error', e);
      return false;
    }
  }, [getPaymentsClient, getBaseCardPaymentMethod]);

  const buildPaymentDataRequest = useCallback(() => ({
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods: [
      {
        ...getBaseCardPaymentMethod(),
        tokenizationSpecification: getTokenizationSpecification()
      }
    ],
    transactionInfo: getTransactionInfo(),
    merchantInfo: {
      ...(merchantName ? { merchantName } : {}),
      ...(merchantId ? { merchantId } : {})
    }
  }), [getBaseCardPaymentMethod, getTokenizationSpecification, getTransactionInfo, merchantName, merchantId]);

  const loadPaymentData = useCallback(async () => {
    try {
      const client = getPaymentsClient();
      const paymentDataRequest = buildPaymentDataRequest();
      console.log('useGooglePay.loadPaymentData request', JSON.stringify(paymentDataRequest, null, 2));
      const paymentData = await client.loadPaymentData(paymentDataRequest);
      const info =
        paymentData &&
        paymentData.paymentMethodData &&
        paymentData.paymentMethodData.info;
      const ecv2Token =
        paymentData &&
        paymentData.paymentMethodData &&
        paymentData.paymentMethodData.tokenizationData &&
        paymentData.paymentMethodData.tokenizationData.token;
      if (!ecv2Token) {
        console.error('useGooglePay.loadPaymentData error: token de Google Pay vacio');
        return null;
      }
      return { token: ecv2Token, cardInfo: info || null };
    } catch (e) {
      if (e && e.statusCode === 'CANCELED') {
        return null;
      }
      console.error('useGooglePay.loadPaymentData error', e);
      return null;
    }
  }, [getPaymentsClient, buildPaymentDataRequest]);

  useEffect(() => {
    let active = true;
    loadGooglePaySdk()
      .then(() => { if (active) { setSdkReady(true); setSdkError(null); } })
      .catch((e) => {
        if (active) {
          setSdkReady(false);
          setSdkError(e);
          console.error('useGooglePay.loadGooglePaySdk error', e);
        }
      });
    return () => { active = false; };
  }, []);

  return {
    isEnvironmentValid,
    environment,
    sdkReady,
    sdkError,
    allowedCardNetworks,
    isReadyToPay,
    loadPaymentData
  };
};
